import React, { useEffect, useState, useMemo } from 'react'
import { FileText, CheckCircle, Building2, Eye, FileCheck, Download } from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { ExchangeAgreementModal } from '../components/documents/ExchangeAgreementModal'
import { useAuth } from '../contexts/AuthContext'
import { useHubData } from '../hooks/useHubData'
import { eSigning } from '../services/eSigning'
import { activityLog } from '../services/activityLog'


interface DocumentStatus {
  name: string
  phase: 'initial' | 'exchange' | 'acquisition' | 'irs'
  category: 'documents' | 'irs' | 'property' | 'compliance'
  required: boolean
  signed?: boolean
  uploaded?: boolean
  signedDate?: string
  signedBy?: string
}

export const DocumentsPage: React.FC = () => {
  const { user } = useAuth()
  const initialData = useHubData('initial')
  const acquisitionData = useHubData('acquisition')
  const exchangeData = useHubData('exchange')
  const [showExchangeAgreement, setShowExchangeAgreement] = useState(false)
  
  const allRequiredDocuments: DocumentStatus[] = useMemo(() => [
    { name: 'Exchange Agreement', phase: 'initial', category: 'documents', required: true },
    { name: 'Assignment of Relinquished Property Contract', phase: 'initial', category: 'documents', required: true },
    { name: 'Notice of Assignment (Buyer)', phase: 'initial', category: 'documents', required: true },
    { name: 'W-9 Tax ID Certification', phase: 'initial', category: 'irs', required: true },
    { name: 'Purchase/Sale Agreement (Relinquished)', phase: 'initial', category: 'property', required: true },
    { name: 'Preliminary Title Report', phase: 'initial', category: 'property', required: false },
    { name: 'Settlement Statement (Relinquished - HUD-1)', phase: 'exchange', category: 'documents', required: true },
    { name: 'Identification of Replacement Property Form', phase: 'exchange', category: 'compliance', required: true },
    { name: 'Purchase Agreement (Replacement)', phase: 'acquisition', category: 'property', required: true },
    { name: 'Assignment of Replacement Property Contract', phase: 'acquisition', category: 'documents', required: true },
    { name: 'Notice of Assignment (Seller)', phase: 'acquisition', category: 'documents', required: true },
    { name: 'Settlement Statement (Replacement - HUD-1)', phase: 'acquisition', category: 'documents', required: true },
    { name: 'Exchange Closing Statement (Relinquished)', phase: 'irs', category: 'irs', required: true },
    { name: 'Exchange Closing Statement (Replacement)', phase: 'irs', category: 'irs', required: true },
    { name: 'Exchange Transaction Report', phase: 'irs', category: 'irs', required: true },
    { name: 'IRS Form 8824 (Like-Kind Exchanges)', phase: 'irs', category: 'irs', required: true }
  ], [])

  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const documentsWithStatus = useMemo(() => {
    const statusMap = new Map<string, DocumentStatus>()
    
    allRequiredDocuments.forEach(doc => {
      statusMap.set(doc.name, { ...doc })
    })
    
    const allHubData = [
      { data: initialData.data, scope: 'initial' },
      { data: acquisitionData.data, scope: 'acquisition' },
      { data: exchangeData.data, scope: 'exchange' }
    ]
    
    const processedDocNames = new Set<string>()
    
    allHubData.forEach(({ data }) => {
      if (data?.documents) {
        data.documents.forEach((doc: any) => {
          if (!processedDocNames.has(doc.name)) {
            processedDocNames.add(doc.name)
            const existing = statusMap.get(doc.name)
            if (existing) {
              existing.signed = doc.signed || existing.signed
              existing.uploaded = doc.uploaded || existing.uploaded
              existing.signedDate = doc.signedDate || existing.signedDate
              existing.signedBy = doc.signedBy || existing.signedBy
            }
          }
        })
      }
    })
    
    // Check for e-signed documents from Sales Hub
    const exchangeAgreementSigned = localStorage.getItem('exchange_agreement_signed') === 'true'
    const w9Signed = localStorage.getItem('w9_signed') === 'true'
    const signedData = localStorage.getItem('exchange_agreement_data')
    let agreementData: any = null
    if (signedData) {
      try {
        agreementData = JSON.parse(signedData)
      } catch (e) {}
    }
    
    if (exchangeAgreementSigned) {
      const exchangeAgreement = statusMap.get('Exchange Agreement')
      if (exchangeAgreement) {
        exchangeAgreement.signed = true
        exchangeAgreement.uploaded = true
        exchangeAgreement.signedDate = agreementData?.signedDate || localStorage.getItem('exchange_agreement_signed_date') || new Date().toISOString()
        exchangeAgreement.signedBy = agreementData?.signedBy || localStorage.getItem('exchange_agreement_signed_by') || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User')
      }
    }
    
    if (w9Signed) {
      const w9Doc = statusMap.get('W-9 Tax ID Certification')
      if (w9Doc) {
        w9Doc.signed = true
        w9Doc.uploaded = true
        w9Doc.signedDate = localStorage.getItem('w9_signed_date') || new Date().toISOString()
        w9Doc.signedBy = localStorage.getItem('w9_signed_by') || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User')
      }
    }
    
    const activities = activityLog.getActivities()
    const hasSalesPurchaseAgreement = activities.some(a => a.type === 'document' && a.title.includes('Sales & Purchase Agreement'))
    const hasPreliminaryTitle = activities.some(a => a.type === 'document' && a.title.includes('Preliminary Title Report'))
    
    if (hasSalesPurchaseAgreement) {
      const salesDoc = statusMap.get('Purchase/Sale Agreement (Relinquished)')
      if (salesDoc) {
        salesDoc.uploaded = true
        const uploadActivity = activities.find(a => a.title.includes('Sales & Purchase Agreement'))
        if (uploadActivity) {
          salesDoc.signedDate = uploadActivity.timestamp
        }
      }
    }
    
    if (hasPreliminaryTitle) {
      const titleDoc = statusMap.get('Preliminary Title Report')
      if (titleDoc) {
        titleDoc.uploaded = true
        const uploadActivity = activities.find(a => a.title.includes('Preliminary Title Report'))
        if (uploadActivity) {
          titleDoc.signedDate = uploadActivity.timestamp
        }
      }
    }
    
    // Add e-signed documents
    const eSignedDocs = eSigning.getDocuments()
    eSignedDocs.forEach(doc => {
      const existing = statusMap.get(doc.name)
      if (existing) {
        existing.signed = doc.status === 'signed' || doc.status === 'completed'
        existing.uploaded = true
        if (doc.signedAt) {
          existing.signedDate = doc.signedAt.toISOString()
        }
        if (doc.signedBy) {
          existing.signedBy = doc.signedBy
        }
      }
    })
    
    return Array.from(statusMap.values())
  }, [allRequiredDocuments, initialData.data, acquisitionData.data, exchangeData.data, refreshTrigger, user])

  // Listen for data updates and refresh
  useEffect(() => {
    const handleDataUpdate = () => {
      initialData.loadData()
      acquisitionData.loadData()
      exchangeData.loadData()
      setRefreshTrigger(prev => prev + 1)
    }
    
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.includes('sale_wizard_') || e.key?.includes('exchange_wizard_') || e.key === 'exchange_agreement_signed' || e.key === 'w9_signed' || e.key === 'sales_hub_submitted') {
        handleDataUpdate()
      }
    }
    
    const handleSalesHubCompleted = () => {
      handleDataUpdate()
    }
    
    window.addEventListener('wizardDataUpdated', handleDataUpdate as EventListener)
    window.addEventListener('salesHubCompleted', handleSalesHubCompleted as EventListener)
    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('wizardDataUpdated', handleDataUpdate as EventListener)
      window.removeEventListener('salesHubCompleted', handleSalesHubCompleted as EventListener)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [initialData, acquisitionData, exchangeData])

  const documentsByPhase = useMemo(() => {
    const phases: Record<string, DocumentStatus[]> = {
      'Initial Setup': [],
      'During Exchange': [],
      'Acquisition & IRS': [],
      'IRS Forms': []
    }
    
    const seenInPhase = new Map<string, Set<string>>()
    
    documentsWithStatus.forEach(doc => {
      let phaseName = ''
      if (doc.phase === 'initial') {
        phaseName = 'Initial Setup'
      } else if (doc.phase === 'exchange') {
        phaseName = 'During Exchange'
      } else if (doc.phase === 'acquisition') {
        phaseName = 'Acquisition & IRS'
      } else if (doc.phase === 'irs') {
        phaseName = 'IRS Forms'
      }
      
      if (phaseName) {
        if (!seenInPhase.has(phaseName)) {
          seenInPhase.set(phaseName, new Set())
        }
        const phaseSet = seenInPhase.get(phaseName)!
        if (!phaseSet.has(doc.name)) {
          phaseSet.add(doc.name)
          phases[phaseName].push(doc)
        }
      }
    })
    
    return phases
  }, [documentsWithStatus])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-slate-100 flex items-center gap-3">
          <FileText className="h-7 w-7 text-primary-600 dark:text-primary-400" />
          Documents
        </h1>
        <p className="text-secondary-600 dark:text-slate-400 mt-1">View documents signed and uploaded in your exchange hubs</p>
      </div>

      {/* Documents grouped by phase */}
      <div className="grid grid-cols-1 gap-6">
        {Object.entries(documentsByPhase).map(([phaseName, docs]) => {
          if (docs.length === 0) return null
          
          const getPhaseIcon = () => {
            switch (phaseName) {
              case 'Initial Setup':
                return <Building2 className="h-5 w-5 text-white" />
              case 'During Exchange':
                return <FileCheck className="h-5 w-5 text-white" />
              case 'Acquisition & IRS':
                return <Building2 className="h-5 w-5 text-white" />
              case 'IRS Forms':
                return <FileText className="h-5 w-5 text-white" />
              default:
                return <FileText className="h-5 w-5 text-white" />
            }
          }
          
          const getPhaseColor = () => {
            switch (phaseName) {
              case 'Initial Setup':
                return 'bg-green-600'
              case 'During Exchange':
                return 'bg-blue-600'
              case 'Acquisition & IRS':
                return 'bg-purple-600'
              case 'IRS Forms':
                return 'bg-red-600'
              default:
                return 'bg-slate-600'
            }
          }
          
          const completedCount = docs.filter(d => d.signed || d.uploaded).length
          
          return (
            <Card key={phaseName} className="p-6">
              <div className="flex items-center mb-4">
                <div className={`flex-shrink-0 h-10 w-10 rounded-lg ${getPhaseColor()} flex items-center justify-center`}>
                  {getPhaseIcon()}
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-slate-100">{phaseName}</h3>
                  <p className="text-xs text-secondary-600 dark:text-slate-400">
                    {completedCount} of {docs.length} document{docs.length !== 1 ? 's' : ''} completed
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {docs.map((doc) => {
                  const isCompleted = doc.signed || doc.uploaded
                  const getDownloadUrl = () => {
                    // For e-signed documents, generate a proper PDF-like content
                    if (doc.name === 'Exchange Agreement' && doc.signed) {
                      const signedData = localStorage.getItem('exchange_agreement_data')
                      let agreementData: any = null
                      if (signedData) {
                        try {
                          agreementData = JSON.parse(signedData)
                        } catch (e) {}
                      }
                      
                      // Get exchanger data from sales hub (initialData) or agreement data
                      const exchangerName = agreementData?.userData?.firstName && agreementData?.userData?.lastName
                        ? `${agreementData.userData.firstName} ${agreementData.userData.lastName}`
                        : initialData.data?.exchangerPhaseOne?.names
                        ? initialData.data.exchangerPhaseOne.names
                        : user?.firstName && user?.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : doc.signedBy || 'User'
                      
                      const exchangerAddress = agreementData?.userData?.address?.street && agreementData?.userData?.address?.city
                        ? `${agreementData.userData.address.street}, ${agreementData.userData.address.city}, ${agreementData.userData.address.state || ''} ${agreementData.userData.address.zipCode || ''}`
                        : initialData.data?.exchangerPhaseOne?.homeAddress
                        ? initialData.data.exchangerPhaseOne.homeAddress
                        : user?.address
                        ? `${user.address.street || ''}, ${user.address.city || ''}, ${user.address.state || ''} ${user.address.zipCode || ''}`
                        : ''
                      
                      const taxId = agreementData?.userData?.ssn || initialData.data?.exchangerPhaseOne?.taxIdNumber || user?.ssn || ''
                      const propertyAddress = initialData.data?.relinquished?.addressOrLegalDescription || ''
                      
                      const pdfContent = `
EXCHANGE AGREEMENT
Qualified Intermediary Services

Date: ${doc.signedDate ? new Date(doc.signedDate).toLocaleDateString() : new Date().toLocaleDateString()}

This Exchange Agreement ("Agreement") is entered into on ${doc.signedDate ? new Date(doc.signedDate).toLocaleDateString() : new Date().toLocaleDateString()} 
between ${exchangerName} ("Exchanger") and Open1031 Platform ("Qualified Intermediary").

EXCHANGER INFORMATION:
Name: ${exchangerName}
Address: ${exchangerAddress}
Tax ID: ${taxId ? taxId.replace(/./g, '*').slice(0, -4) + taxId.slice(-4) : '***-**-****'}

${propertyAddress ? `RELINQUISHED PROPERTY:
${propertyAddress}

` : ''}KEY TERMS:
- The Qualified Intermediary agrees to facilitate the tax-deferred exchange pursuant to Section 1031 of the Internal Revenue Code
- The Exchanger assigns all rights to the Relinquished Property sale proceeds to the Qualified Intermediary
- Exchange must be completed within 180 days of the Relinquished Property sale
- Replacement properties must be identified within 45 days
- All exchange funds will be held in a segregated qualified escrow account

SIGNATURE:
${agreementData?.signature || 'Signed Electronically'}

Signed by: ${doc.signedBy || exchangerName}
Signed on: ${doc.signedDate ? new Date(doc.signedDate).toLocaleDateString() : new Date().toLocaleDateString()}

This document has been electronically signed and is valid for 1031 exchange purposes.
`
                      const blob = new Blob([pdfContent], { type: 'application/pdf' })
                      return URL.createObjectURL(blob)
                    } else if (doc.name === 'W-9 Tax ID Certification' && doc.signed) {
                      // Get W-9 data from sales hub (initialData) or user data
                      const w9Name = initialData.data?.exchangerPhaseOne?.names
                        ? initialData.data.exchangerPhaseOne.names
                        : user?.firstName && user?.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : doc.signedBy || 'User'
                      
                      const w9Address = initialData.data?.exchangerPhaseOne?.homeAddress
                        ? initialData.data.exchangerPhaseOne.homeAddress
                        : user?.address
                        ? `${user.address.street || ''}, ${user.address.city || ''}, ${user.address.state || ''} ${user.address.zipCode || ''}`
                        : ''
                      
                      const w9TaxId = initialData.data?.exchangerPhaseOne?.taxIdNumber || user?.ssn || ''
                      
                      const pdfContent = `
FORM W-9
(Rev. October 2018)
Department of the Treasury
Internal Revenue Service

Request for Taxpayer Identification Number and Certification

Name: ${w9Name}
Business name/disregarded entity name (if applicable): 

Address: ${w9Address}

Taxpayer Identification Number: ${w9TaxId ? w9TaxId.replace(/./g, '*').slice(0, -4) + w9TaxId.slice(-4) : '***-**-****'}

SIGNATURE:
${localStorage.getItem('w9_signature') || 'Signed Electronically'}

Signed by: ${doc.signedBy || w9Name}
Signed on: ${doc.signedDate ? new Date(doc.signedDate).toLocaleDateString() : new Date().toLocaleDateString()}

This document has been electronically signed and is valid for tax purposes.
`
                      const blob = new Blob([pdfContent], { type: 'application/pdf' })
                      return URL.createObjectURL(blob)
                    } else {
                      const blob = new Blob([`Mock document: ${doc.name}`], { type: 'application/pdf' })
                      return URL.createObjectURL(blob)
                    }
                  }
                  return (
                    <Card key={`${phaseName}-${doc.name}`} className={`p-4 ${!isCompleted ? 'opacity-60' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {isCompleted ? (
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <FileText className="h-5 w-5 text-secondary-400" />
                          )}
                          <div>
                            <div className="flex items-center space-x-2 flex-wrap">
                              <div className="text-sm font-medium text-secondary-900 dark:text-slate-100">{doc.name}</div>
                              {doc.required && (
                                <Badge variant="secondary" className="text-xs">Required</Badge>
                              )}
                              {doc.signed && (
                                <Badge className="bg-green-600 text-white">Signed</Badge>
                              )}
                              {doc.uploaded && !doc.signed && (
                                <Badge variant="success">Uploaded</Badge>
                              )}
                            </div>
                            {doc.signed && doc.signedDate && (
                              <div className="text-xs text-secondary-600 dark:text-slate-400 mt-1">
                                Signed on {new Date(doc.signedDate).toLocaleDateString()}
                                {doc.signedBy && ` by ${doc.signedBy}`}
                              </div>
                            )}
                            {!isCompleted && (
                              <div className="text-xs text-secondary-500 dark:text-slate-500 mt-1">
                                Sign or upload in the {phaseName} hub
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {isCompleted ? (
                            <>
                              {doc.name === 'Exchange Agreement' && doc.signed ? (
                                <>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setShowExchangeAgreement(true)}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    View
                                  </Button>
                                  <a href={getDownloadUrl()} download={`${doc.name.replace(/\s+/g, '_')}_Signed.pdf`}>
                                    <Button variant="outline" size="sm">
                                      <Download className="h-4 w-4 mr-1" />
                                      Download
                                    </Button>
                                  </a>
                                </>
                              ) : doc.name === 'W-9 Tax ID Certification' && doc.signed ? (
                                <a href={getDownloadUrl()} download={`W9_Tax_ID_Certification_Signed.pdf`}>
                                  <Button variant="outline" size="sm">
                                    <Download className="h-4 w-4 mr-1" />
                                    Download
                                  </Button>
                                </a>
                              ) : (
                                <a href={getDownloadUrl()} download={`${doc.name.replace(/\s+/g, '_')}.pdf`}>
                                  <Button variant="outline" size="sm">
                                    <Download className="h-4 w-4 mr-1" />
                                    Download
                                  </Button>
                                </a>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-secondary-500 dark:text-slate-500">Pending</span>
                          )}
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            </Card>
          )
        })}
      </div>

      {/* Exchange Agreement Modal */}
      {showExchangeAgreement && user && (() => {
        const signedData = localStorage.getItem('exchange_agreement_data')
        let agreementData: any = null
        if (signedData) {
          try {
            agreementData = JSON.parse(signedData)
          } catch (e) {}
        }
        
        return (
          <ExchangeAgreementModal
            isOpen={showExchangeAgreement}
            onClose={() => setShowExchangeAgreement(false)}
            onSignComplete={() => {
              setShowExchangeAgreement(false)
              // Refresh the page to show updated status
              window.location.reload()
            }}
            userData={agreementData?.userData || {
              firstName: user.firstName || 'John',
              lastName: user.lastName || 'Doe',
              email: user.email || 'john.doe@example.com',
              address: {
                street: user.address?.street || '123 Main Street',
                city: user.address?.city || 'San Francisco',
                state: user.address?.state || 'CA',
                zipCode: user.address?.zipCode || '94102',
              },
              ssn: user.ssn,
            }}
          />
        )
      })()}
    </div>
  )
}
